/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

export interface NotificationCard {
	id: string;
	title: string;
	description?: string;
	link?: string;
	endDate?: string;
	createdAt?: string;
	publishedAt?: string;
	updatedAt?: string;
}

export interface RawNotification {
	id: number;
	title: string;
	description?: string;
	link?: string;
	endDate?: string;
	created_at?: string;
	published_at?: string;
	updated_at?: string;
}

/**
 * Normalizes Strapi notification entries to a UI friendly shape.
 * @param rawNotification Strapi notification entry from the backend.
 * @returns Normalized notification card or undefined if mandatory fields are missing.
 * @author Julien Tietke <j.tietke@tj-labs.de>
 */
export const normalizeNotification = (rawNotification: RawNotification): NotificationCard | undefined => {
	if (!rawNotification.title) {
		return undefined;
	}

	return {
		id: String(rawNotification.id),
		title: rawNotification.title,
		description: rawNotification.description,
		link: rawNotification.link,
		endDate: rawNotification.endDate,
		createdAt: rawNotification.created_at,
		publishedAt: rawNotification.published_at,
		updatedAt: rawNotification.updated_at,
	};
};

/**
 * Extracts a raw notification array from a Strapi response payload.
 * @param payload Response payload returned by Strapi.
 * @returns Array of raw notification entries.
 * @author Julien Tietke <j.tietke@tj-labs.de>
 */
export const extractRawNotifications = (payload: unknown): RawNotification[] => {
	if (!payload) {
		return [];
	}

	if (Array.isArray(payload)) {
		return payload;
	}

	const typedPayload = payload as { data?: RawNotification[] };

	if (Array.isArray(typedPayload.data)) {
		return typedPayload.data;
	}

	return [];
};
